import React from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, Search, Download, Bell, LogOut, ChevronDown, Menu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { exportPosts } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "map", label: "Geospatial Map" },
  { k: "analysis", label: "Analysis" },
  { k: "feed", label: "Live Feed" },
];

export default function NavigationRow() {
  const { activeTab, setActiveTab, filters, patchFilters, alerts, source } = useApp();
  const { user, role, switchRole, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-white/10" data-testid="navigation-row">
      <div className="flex items-center justify-between w-full md:w-auto px-3 py-1.5 md:py-0 md:h-12 border-b md:border-b-0 md:border-r border-white/10">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Adani Intelligence" className="w-6 h-6 object-contain" />
          <div className="font-display text-xs tracking-[0.2em] uppercase hidden sm:block">Adani Intelligence</div>
          <span
            data-testid="data-source-indicator"
            className={`label-micro px-1.5 py-0.5 border ${source === "live" ? "border-[var(--sev-low)] text-[var(--sev-low)]" : "border-white/20 text-white/50"}`}
          >
            {source === "live" ? "LIVE" : "DEMO"}
          </span>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 border border-white/10">
                <Bell className="w-4 h-4" />
                {alerts.length > 0 && <span className="absolute top-0 right-0 w-2 h-2 bg-[var(--sev-critical)] rounded-full" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-[var(--bg-surface)] border-white/15 text-white">
              {/* Alert content same as desktop */}
              <div className="label-micro mb-2">High-Risk Alerts</div>
              <div className="max-h-64 overflow-y-auto">
                {alerts.map(a => (
                  <div key={a.id} className="p-2 border-b border-white/5 text-[11px]">
                    <div className="text-[var(--sev-critical)] uppercase">{a.venture}</div>
                    <div className="line-clamp-1">{a.content}</div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Sheet>
            <SheetTrigger asChild>
              <button className="p-2 border border-white/10">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[var(--bg-app)] border-r border-white/10 p-0 w-72">
              <div className="p-4 border-b border-white/10 flex items-center gap-2">
                <img src="/logo.png" alt="Logo" className="w-6 h-6" />
                <span className="font-display text-sm tracking-widest">COMMAND CENTER</span>
              </div>
              <div className="py-2">
                {TABS.map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setActiveTab(t.k)}
                    className={`w-full text-left px-4 py-3 text-xs uppercase tracking-widest border-b border-white/5 transition-colors ${
                      activeTab === t.k ? "bg-accent-strong" : "hover:bg-white/5"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
                <button onClick={logout} className="w-full flex items-center gap-2 py-2 text-xs text-white/50 hover:text-white mt-2">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="hidden md:flex items-center flex-1">
        <nav className="flex h-full" data-testid="nav-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`nav-tab-${t.k}`}
              onClick={() => setActiveTab(t.k)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.15em] border-r border-white/10 transition-colors ${
                activeTab === t.k
                  ? "bg-accent-strong"
                  : "bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 md:py-0 md:h-12 overflow-x-auto">
        <div className="relative flex-1 md:flex-none">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <Input
            data-testid="search-input"
            placeholder="Search..."
            value={filters.q}
            onChange={(e) => patchFilters({ q: e.target.value })}
            className="h-8 pl-8 w-32 sm:w-48 lg:w-64 rounded-none bg-[var(--bg-surface)] border-white/15 text-xs"
          />
        </div>
        
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => exportPosts(filters)}
            className="flex items-center gap-1.5 px-2.5 h-8 text-xs border border-white/15 hover:border-white/30 hover:bg-white/5"
          >
            <Download className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Export</span>
          </button>

          <Popover>
            <PopoverTrigger asChild>
              <button className="relative flex items-center gap-1.5 px-2.5 h-8 text-xs border border-white/15 hover:border-white/30">
                <Bell className="w-3.5 h-3.5" />
                {alerts.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[var(--sev-critical)] flex items-center justify-center text-[9px] text-white">{alerts.length}</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-[var(--bg-surface)] border-white/15 text-white">
              <div className="p-3 border-b border-white/10 label-micro">Active Alerts</div>
              <div className="max-h-80 overflow-y-auto p-2">
                {alerts.map(a => (
                  <div key={a.id} className="p-2 border-b border-white/5 hover:bg-white/5">
                    <div className="text-[var(--sev-critical)] text-[10px] uppercase font-bold">{a.venture}</div>
                    <div className="text-xs text-white/80">{a.content}</div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>


        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-2 h-8 border border-white/15">
              <div className="w-5 h-5 bg-white/10 flex items-center justify-center text-[10px] font-bold">{user?.name?.slice(0,1)}</div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[var(--bg-surface)] border-white/15 text-white">
            <DropdownMenuLabel className="text-xs">{user?.name}</DropdownMenuLabel>
            <DropdownMenuItem onClick={logout} className="text-xs"><LogOut className="w-3 h-3 mr-2" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
